import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useDriver } from "../context/DriverContext";
import axios from "axios";

export default function DriverProfile() {
  const { driver, setDriver, } = useDriver();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [infoSuccess, setInfoSuccess] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [error, setError] = useState("");

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  // Initialize name when driver loads
  useEffect(() => {
    if (driver) setName(driver.name || "");
  }, [driver]);

  const handleInfoUpdate = async () => {
    if (!name) return setError("Name is required.");
    setError(""); // clear previous errors
    try {
      const { data } = await axios.patch(
        `${BASE_URL}/api/driver/profile`,
        { name },
        { withCredentials: true }
      );

      // Update context with new driver info
      setDriver(data.driver);

      // Show success message from backend
      setInfoSuccess(data.message || "Profile updated successfully!");
      setTimeout(() => setInfoSuccess(""), 4000);

    } catch (err) {
      console.error("Profile update error:", err);
      setError(err.response?.data?.message || "Failed to update profile.");
    }
  };


  const handlePasswordUpdate = async () => {
    setError("");
    setPasswordSuccess(""); // clear previous success messages

    if (!currentPassword || !newPassword || !confirmPassword) {
      return setError("Please fill out all password fields.");
    }
    if (newPassword !== confirmPassword) {
      return setError("New passwords do not match.");
    }

    try {
      await axios.patch(
        `${BASE_URL}/api/driver/profile/change-password`,
        { currentPassword, newPassword },
        { withCredentials: true }
      );

      setPasswordSuccess("Password updated successfully. Logging out...");
      setTimeout(() => {
        setDriver(null);       // clear context
        navigate("/driver-login"); // redirect to login
      }, 3000);
    } catch (err) {
      console.error("Password update error:", err);
      setError(err.response?.data?.message || "Failed to update password.");
    }
  };

  if (!driver) return null; // or a loader

  return (
    <div className="w-full max-w-4xl mx-auto bg-white p-6 md:p-8 mt-8 rounded shadow">
      <h2 className="text-2xl font-bold mb-6">Edit Profile</h2>

      {/* Display errors */}
      {error && (
        <div className="bg-red-100 text-red-700 text-sm px-3 py-2 rounded mb-4">
          {error}
        </div>
      )}

      {/* Profile Info */}
      <div className="mb-10">
        <h3 className="text-lg font-semibold mb-4">Profile Info</h3>
        {infoSuccess && (
          <div className="bg-green-100 text-green-700 text-sm px-3 py-2 rounded mb-4">
            {infoSuccess}
          </div>
        )}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Name <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Email
          </label>
          <input
            type="email"
            value={driver.email}
            readOnly
            className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-600 cursor-not-allowed"
          />
        </div>

        <button
          onClick={handleInfoUpdate}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
        >
          Update Info
        </button>
      </div>

      {/* Change Password */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Change Password</h3>
        {passwordSuccess && (
          <div className="bg-green-100 text-green-700 text-sm px-3 py-2 rounded mb-4">
            {passwordSuccess}
          </div>
        )}

        <div className="mb-4 relative">
          <label className="block text-sm font-medium mb-1">
            Current Password
          </label>
          <input
            type={showCurrent ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full border rounded px-3 py-2 pr-10"
          />
          <span
            className="absolute top-8 right-3 cursor-pointer text-gray-500"
            onClick={() => setShowCurrent(!showCurrent)}
          >
            {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
          </span>
        </div>

        <div className="mb-4 relative">
          <label className="block text-sm font-medium mb-1">
            New Password
          </label>
          <input
            type={showNew ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full border rounded px-3 py-2 pr-10"
          />
          <span
            className="absolute top-8 right-3 cursor-pointer text-gray-500"
            onClick={() => setShowNew(!showNew)}
          >
            {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
          </span>
        </div>

        <div className="mb-6 relative">
          <label className="block text-sm font-medium mb-1">
            Confirm New Password
          </label>
          <input
            type={showConfirm ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full border rounded px-3 py-2 pr-10"
          />
          <span
            className="absolute top-8 right-3 cursor-pointer text-gray-500"
            onClick={() => setShowConfirm(!showConfirm)}
          >
            {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
          </span>
        </div>

        <button
          onClick={handlePasswordUpdate}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-500"
        >
          Update Password
        </button>
      </div>
    </div>
  );
}
